import { TFunction } from 'i18next';
import moment from 'moment';

type TimeRangeFormatOptions = {
    includeOrdinal?: boolean;
    t: TFunction;
};

/**
 * Formats a time range for booking displays according to the spec:
 * - If ongoing: "now – [End date/time]"
 * - If same day: "[Day name] [Day] [Start time] – [End time]"
 * - If different days but same month/year: "[Start day] [Start date] at [Start time] – [End day] [End date] at [End time]"
 * - If different months: "[Start date with month] at [Start time] – [End date with month] at [End time]"
 * - If crossing years: "[Start date, month, year] at [time] – [End date, month, year] at [time]"
 */
const formatBookingTimeRange = (from: Date, to: Date, options: TimeRangeFormatOptions): string => {
    const { includeOrdinal = true, t } = options;

    const fromMoment = moment(from);
    const toMoment = moment(to);
    const now = moment();

    // Check if booking is ongoing (within 5 minutes of start)
    const isOngoing = toMoment.isAfter(now) && fromMoment.diff(now, 'minutes') <= 5;

    if (isOngoing) {
        const endFormat = getRelativeFormat(toMoment, now, now, includeOrdinal, t);
        const nowText = t('serviceExpanded:timeNow');
        return `${nowText} – ${endFormat}`;
    }

    const fromFormat = getRelativeFormat(fromMoment, now, now, includeOrdinal, t);
    const toFormat = getRelativeFormat(toMoment, fromMoment, now, includeOrdinal, t);
    return `${fromFormat} – ${toFormat}`;
};

/**
 * Determines the appropriate format for the end time when booking is ongoing
 */
const getRelativeFormat = (
    endMoment: moment.Moment,
    relative: moment.Moment,
    now: moment.Moment,
    includeOrdinal: boolean,
    t: TFunction,
): string => {
    const isThisWeek = endMoment.isSame(now, 'week');
    const isThisMonth = endMoment.isSame(now, 'month');
    const isSameDay = endMoment.isSame(relative, 'day');
    const isSameWeek = endMoment.isSame(relative, 'week');
    const isSameMonth = endMoment.isSame(relative, 'month');
    const isSameYear = endMoment.isSame(relative, 'year');

    const clock = endMoment.format('HH:mm');
    if (isSameDay) {
        return clock;
    }
    const atClock = `${t('global:clock')} ${clock}`;
    if (isSameWeek) {
        if (isSameMonth) {
            if (isThisWeek) {
                return `${endMoment.format('dddd')} ${atClock}`;
            }
            if (isThisMonth) {
                const format = includeOrdinal ? 'dddd Do' : 'dddd D';
                return `${endMoment.format(format)} ${atClock}`;
            }
            const format = includeOrdinal ? 'Do' : 'D';
            return `${endMoment.format(format)} ${atClock}`;
        }
        const format = includeOrdinal ? 'dddd Do MMM' : 'dddd D MMM';
        return `${endMoment.format(format)} ${atClock}`;
    }

    if (isSameMonth) {
        const format = includeOrdinal ? 'dddd Do' : 'dddd D';
        return `${endMoment.format(format)} ${atClock}`;
    }

    if (isSameYear) {
        const format = includeOrdinal ? 'Do MMM' : 'D MMM';
        return `${endMoment.format(format)} ${atClock}`;
    }

    const format = includeOrdinal ? 'Do MMM YYYY' : 'D MMM YYYY';
    return `${endMoment.format(format)} ${atClock}`;
};

export default formatBookingTimeRange;
